import React, { useState } from 'react';
import '../BuyerSelectionPopup.scss';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementData } from '../../../../slices/marketplace/timeline/models/engagement-data.model';
import { EngagementProcess } from '../../../../slices/marketplace/timeline/models/engagement-process.model';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { EngagementBuyer } from '../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { MarketplaceOpportunityStatus } from '../../../../slices/marketplace/opportunity/models/opportunity-status';
import { useStateSelector } from '../../../../store/selectors';
import { LevelOfService } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import Helpers from '../../../../utils/helper';
import { TimelineActionStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import './IdNdaSelectionForm.scss';
import { TimelineEventStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { EngagementProcessStatus } from '../../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';

interface IdNdaSelectionFormProps {
    events: TimelineEvent[];
    targetEvent: TimelineEvent;
    engagementData: EngagementData;
    engagementProcess: EngagementProcess;
    engagementId: number;
    setSelectedBuyerId(value: number): void;
    setIsBuyerProfilePopupVisible(value: boolean): void;
    isLoading: boolean;
    onPayAndSendClick(selectedBuyerIds: number[]): void;
    onCancelClick(): void;
    actionId: number;
}

export const IdNdaSelectionForm = (props: IdNdaSelectionFormProps) => {
    const [selectedBuyerIds, setSelectedBuyerIds] = useState<number[]>([]);
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );

    const columns = [
        {
            dataField: 'companyName',
            text: 'Buyer',
            sort: false,
            formatter: (cellContent: string, currentBuyer: EngagementBuyer) => (
                <div
                    className="link-text"
                    onClick={() => {
                        props.setSelectedBuyerId(currentBuyer.buyerId);
                        props.setIsBuyerProfilePopupVisible(true);
                    }}>
                    {cellContent}
                </div>
            ),
        },
        {
            dataField: 'id',
            text: 'Status',
            sort: false,
            formatter: (
                _cellContent: number,
                currentBuyer: EngagementBuyer
            ) => {
                const buyerStates = currentBuyer.actionState;
                const currentActionState = buyerStates.find(
                    (x) => x.actionId === props.actionId
                );
                if (!currentActionState) {
                    return <span>Not Sent</span>;
                }
                if (
                    currentActionState.status !== TimelineActionStatus.Completed
                ) {
                    return <span>Provided</span>;
                }

                return <span>Executed</span>;
            },
        },
        {
            dataField: 'buyerId',
            text: 'Send',
            sort: false,
            formatter: (cellContent: number, currentBuyer: EngagementBuyer) => {
                const buyerStates = currentBuyer.actionState;
                const currentActionState = buyerStates.find(
                    (x) => x.actionId === props.actionId
                );
                const isBuyerSelected =
                    currentActionState != null &&
                    currentActionState.buyerStatus ===
                        MarketplaceOpportunityStatus.Engaged;
                return isBuyerSelected === true ? (
                    <></>
                ) : (
                    <input
                        type="checkbox"
                        checked={selectedBuyerIds.includes(cellContent)}
                        disabled={
                            props.targetEvent?.status ===
                                TimelineEventStatus.Completed ||
                            props.engagementProcess?.status ===
                                EngagementProcessStatus.Completed
                        }
                        onChange={(event) => {
                            const isChecked = event.target.checked;
                            if (isChecked === true) {
                                setSelectedBuyerIds([
                                    ...selectedBuyerIds,
                                    cellContent,
                                ]);
                            } else {
                                setSelectedBuyerIds(
                                    selectedBuyerIds.filter(
                                        (id) => id !== cellContent
                                    )
                                );
                            }
                        }}
                    />
                );
            },
        },
    ];

    const renderTotalPriceRow = () => {
        const selectedBuyersLength = selectedBuyerIds.length;
        const levelOfService = props.engagementProcess.levelOfService;

        let pricePerBuyer = 0;

        if (levelOfService === LevelOfService.DiscountBrokerage) {
            pricePerBuyer =
                marketplacePricing.discountBrokeragePricing.transactionFees
                    .idNdaFeePerBuyer;
        } else if (levelOfService === LevelOfService.FullServiceBrokerage) {
            pricePerBuyer =
                marketplacePricing.fullServiceBrokeragePricing.transactionFees
                    .idNdaFeePerBuyer;
        }

        const totalPrice = pricePerBuyer * selectedBuyersLength;
        return (
            <div>
                Total Cost: {Helpers.GetCurrencyFormat(totalPrice, 2, true)}
            </div>
        );
    };

    return (
        <div className="id-nda-selection-form">
            <BootstrapTable
                striped
                key={selectedBuyerIds.length}
                bordered
                bootstrap4
                keyField="Id"
                data={
                    props.engagementProcess?.buyers?.filter(
                        (x) => x.isActive
                    ) ?? []
                }
                columns={columns}
                noDataIndication="No Data available"
            />
            {renderTotalPriceRow()}
            <div className="buttons-wrapper">
                <button
                    className="btn btn-cancel"
                    disabled={props.isLoading}
                    onClick={() => {
                        props.onCancelClick();
                    }}>
                    Cancel
                </button>
                <ButtonLoader
                    buttonText={'Pay & Send'}
                    loaderButtonText={''}
                    disabled={props.isLoading || selectedBuyerIds.length === 0}
                    isLoading={props.isLoading}
                    onClick={() => {
                        props.onPayAndSendClick(selectedBuyerIds);
                    }}
                    className={'btn btn-primary'}
                />
            </div>
        </div>
    );
};
